import { useState, useEffect } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import MenuEditor from '../components/MenuEditor';
import { 
  Save, 
  Send, 
  Download, 
  ArrowLeft,
  Calendar,
  Users,
  CreditCard,
  User,
  Utensils,
  Music,
  Monitor,
  Info,
  Clock,
  MapPin,
  Database,
  Euro
} from 'lucide-react';

const VENUE_LOCATIONS = [
  { value: "Gastraum", label: "Gastraum" },
  { value: "Ratsherrenstube", label: "Ratsherrenstube" },
  { value: "Rotbierkeller", label: "Rotbierkeller" },
  { value: "Tigerhöhle", label: "Tigerhöhle" },
  { value: "Komplettbuchung", label: "Komplettbuchung" },
  { value: "Terrasse König", label: "Terrasse König" },
  { value: "Terrasse Zeughaus", label: "Terrasse Zeughaus" },
];

const TMK_CONTACTS = [
  { value: "Sari Pech", label: "Sari Pech" },
  { value: "Lena Schips", label: "Lena Schips" },
  { value: "Simon Röschke", label: "Simon Röschke" },
  { value: "Andreas Röschke", label: "Andreas Röschke" },
  { value: "Agentur", label: "Agentur" },
];

const defaultVaFacts = {
  title: '',
  event_date: '',
  event_time: '',
  customer: '',
  guest_count: '',
  venue_location: '',
  venue_exclusive: false,
  venue_notes: '',
  artists: '',
  technical: '',
  payment_method: '',
  billing_address: '',
  contact_tmk: '',
  contact_person: '',
  contact_details: '',
  schedule: '',
  menu: '',
  other_info: '',
  gastronovi_customer: '',
  gastronovi_customer_number: '',
  billing_menu_price: '',
  billing_drinks: '',
  billing_technical: '',
  billing_deco: '',
  billing_latenight: '',
  billing_artists: '',
  billing_other: '',
  billing_min_pax: ''
};

const EditorPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { id } = useParams();
  const { api } = useAuth();
  
  const [vaFacts, setVaFacts] = useState(defaultVaFacts);
  const [orderId, setOrderId] = useState(null);
  const [sheetId, setSheetId] = useState(null);
  const [status, setStatus] = useState('draft');
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (id) {
      loadSheet(id);
    } else if (location.state?.vaFacts) {
      setVaFacts({ ...defaultVaFacts, ...location.state.vaFacts });
      setOrderId(location.state.orderId);
    }
  }, [id, location.state]);

  const loadSheet = async (sheetId) => {
    setLoading(true);
    try {
      const response = await api.get(`/function-sheets/${sheetId}`);
      setVaFacts(response.data.va_facts);
      setSheetId(response.data.id);
      setOrderId(response.data.order_confirmation_id);
      setStatus(response.data.status);
    } catch (error) {
      toast.error('Function Sheet konnte nicht geladen werden');
      navigate('/sheets');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setVaFacts(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async (newStatus = null) => {
    setSaving(true);
    try {
      const payload = {
        va_facts: vaFacts,
        status: newStatus || status,
        order_confirmation_id: orderId
      };

      let response;
      if (sheetId) {
        response = await api.put(`/function-sheets/${sheetId}`, payload);
        toast.success('Function Sheet gespeichert!');
      } else {
        response = await api.post('/function-sheets', payload);
        setSheetId(response.data.id);
        toast.success('Function Sheet erstellt!');
      }

      if (newStatus) {
        setStatus(newStatus);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Speichern fehlgeschlagen');
    } finally {
      setSaving(false);
    }
  };

  const handlePublish = () => handleSave('published');
  const handleSaveDraft = () => handleSave('draft');

  const handleDownload = async () => {
    if (!sheetId) {
      toast.error('Bitte zuerst speichern');
      return;
    }

    try {
      const response = await api.get(`/function-sheets/${sheetId}/download`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `FunctionSheet_${vaFacts.title || 'Veranstaltung'}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('PDF heruntergeladen!');
    } catch (error) {
      toast.error('Download fehlgeschlagen');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-pulse text-burgundy">Wird geladen...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="editor-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            onClick={() => navigate(-1)}
            className="text-charcoal-600 hover:text-burgundy"
            data-testid="back-btn"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Zurück
          </Button>
          <div>
            <h1 className="font-serif text-2xl md:text-3xl font-bold text-burgundy">
              {sheetId ? 'Function Sheet bearbeiten' : 'Neues Function Sheet'}
            </h1>
            {sheetId && (
              <Badge 
                className={status === 'published' 
                  ? 'bg-emerald-100 text-emerald-700 mt-1' 
                  : 'bg-gold-100 text-gold-700 mt-1'
                }
              >
                {status === 'published' ? 'Veröffentlicht' : 'Entwurf'}
              </Badge>
            )}
          </div>
        </div>
        
        <div className="flex gap-3">
          <Button
            variant="outline"
            onClick={handleSaveDraft}
            disabled={saving}
            className="border-stone-300"
            data-testid="save-draft-btn"
          >
            <Save className="h-4 w-4 mr-2" />
            Speichern
          </Button>
          <Button
            onClick={handlePublish}
            disabled={saving}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid="publish-btn"
          >
            <Send className="h-4 w-4 mr-2" />
            Veröffentlichen
          </Button>
          {sheetId && (
            <Button
              variant="outline"
              onClick={handleDownload}
              className="border-burgundy text-burgundy hover:bg-burgundy-50"
              data-testid="download-btn"
            >
              <Download className="h-4 w-4 mr-2" />
              PDF
            </Button>
          )}
        </div>
      </div>

      {/* Editor Form */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left Column - Basic Info */}
        <div className="space-y-6">
          <Card className="shadow-card">
            <CardHeader className="border-b border-stone-100 bg-stone-50/50">
              <CardTitle className="font-serif text-lg text-burgundy flex items-center gap-2">
                <Calendar className="h-5 w-5" />
                Veranstaltungsdetails
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6 space-y-4">
              <div className="space-y-2">
                <Label htmlFor="title">Titel der Veranstaltung</Label>
                <Input
                  id="title"
                  value={vaFacts.title}
                  onChange={(e) => handleChange('title', e.target.value)}
                  placeholder="z.B. Firmenweihnachtsfeier Müller GmbH"
                  className="border-stone-300"
                  data-testid="input-title"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="event_date">Datum</Label>
                  <Input
                    id="event_date"
                    value={vaFacts.event_date}
                    onChange={(e) => handleChange('event_date', e.target.value)}
                    placeholder="TT.MM.JJJJ"
                    className="border-stone-300"
                    data-testid="input-date"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="event_time">Uhrzeit</Label>
                  <Input
                    id="event_time"
                    value={vaFacts.event_time}
                    onChange={(e) => handleChange('event_time', e.target.value)}
                    placeholder="z.B. 18:00 - 23:00"
                    className="border-stone-300"
                    data-testid="input-time"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="customer">Kunde</Label>
                  <Input
                    id="customer"
                    value={vaFacts.customer}
                    onChange={(e) => handleChange('customer', e.target.value)}
                    placeholder="Firmenname oder Person"
                    className="border-stone-300"
                    data-testid="input-customer"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="guest_count">Gästezahl</Label>
                  <Input
                    id="guest_count"
                    value={vaFacts.guest_count}
                    onChange={(e) => handleChange('guest_count', e.target.value)}
                    placeholder="z.B. 50"
                    className="border-stone-300"
                    data-testid="input-guests"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="shadow-card">
            <CardHeader className="border-b border-stone-100 bg-stone-50/50">
              <CardTitle className="font-serif text-lg text-burgundy flex items-center gap-2">
                <MapPin className="h-5 w-5" />
                Veranstaltungsort
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="venue_location">Raum / Bereich</Label>
                  <Select
                    value={vaFacts.venue_location}
                    onValueChange={(value) => handleChange('venue_location', value)}
                  >
                    <SelectTrigger className="border-stone-300" data-testid="select-venue">
                      <SelectValue placeholder="Raum wählen" />
                    </SelectTrigger>
                    <SelectContent>
                      {VENUE_LOCATIONS.map((loc) => (
                        <SelectItem key={loc.value} value={loc.value}>
                          {loc.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2 flex items-end">
                  <div className="flex items-center space-x-2 pb-2">
                    <Checkbox
                      id="venue_exclusive"
                      checked={vaFacts.venue_exclusive}
                      onCheckedChange={(checked) => handleChange('venue_exclusive', checked)}
                      data-testid="checkbox-exclusive"
                    />
                    <Label 
                      htmlFor="venue_exclusive" 
                      className="text-sm font-medium leading-none cursor-pointer"
                    >
                      Exklusiv gebucht
                    </Label>
                  </div>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="venue_notes">Platzierungswünsche</Label>
                <Textarea
                  id="venue_notes"
                  value={vaFacts.venue_notes}
                  onChange={(e) => handleChange('venue_notes', e.target.value)}
                  placeholder="z.B. Tische in U-Form, Platz für Tanzfläche, separater Bereich für Buffet..."
                  className="border-stone-300 min-h-[60px]"
                  data-testid="input-venue-notes"
                />
              </div>
            </CardContent>
          </Card>

          <Card className="shadow-card">
            <CardHeader className="border-b border-stone-100 bg-stone-50/50">
              <CardTitle className="font-serif text-lg text-burgundy flex items-center gap-2">
                <User className="h-5 w-5" />
                Ansprechpartner & Abrechnung
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="contact_tmk">Ansprechpartner TMK</Label>
                  <Select
                    value={vaFacts.contact_tmk}
                    onValueChange={(value) => handleChange('contact_tmk', value)}
                  >
                    <SelectTrigger className="border-stone-300" data-testid="select-contact-tmk">
                      <SelectValue placeholder="TMK Mitarbeiter wählen" />
                    </SelectTrigger>
                    <SelectContent>
                      {TMK_CONTACTS.map((contact) => (
                        <SelectItem key={contact.value} value={contact.value}>
                          {contact.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="payment_method">Bezahlart</Label>
                  <Select
                    value={vaFacts.payment_method}
                    onValueChange={(value) => handleChange('payment_method', value)}
                  >
                    <SelectTrigger className="border-stone-300" data-testid="select-payment">
                      <SelectValue placeholder="Bezahlart wählen" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Karte/Bar vor Ort">Karte/Bar vor Ort</SelectItem>
                      <SelectItem value="Rechnung">Rechnung</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="contact_person">Ansprechpartner Kunde</Label>
                  <Input
                    id="contact_person"
                    value={vaFacts.contact_person}
                    onChange={(e) => handleChange('contact_person', e.target.value)}
                    placeholder="Name des Kunden"
                    className="border-stone-300"
                    data-testid="input-contact"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="contact_details">Kontaktdaten Kunde</Label>
                  <Input
                    id="contact_details"
                    value={vaFacts.contact_details}
                    onChange={(e) => handleChange('contact_details', e.target.value)}
                    placeholder="Tel / E-Mail"
                    className="border-stone-300"
                    data-testid="input-contact-details"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="billing_address">Rechnungsadresse</Label>
                <Textarea
                  id="billing_address"
                  value={vaFacts.billing_address}
                  onChange={(e) => handleChange('billing_address', e.target.value)}
                  placeholder="Vollständige Adresse"
                  className="border-stone-300 min-h-[80px]"
                  data-testid="input-billing"
                />
              </div>
            </CardContent>
          </Card>

          <Card className="shadow-card">
            <CardHeader className="border-b border-stone-100 bg-stone-50/50">
              <CardTitle className="font-serif text-lg text-burgundy flex items-center gap-2">
                <Euro className="h-5 w-5" />
                Abrechnen, brutto
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="billing_min_pax">Mindestens Pax abrechnen</Label>
                  <Input
                    id="billing_min_pax"
                    value={vaFacts.billing_min_pax}
                    onChange={(e) => handleChange('billing_min_pax', e.target.value)}
                    placeholder="z.B. 30"
                    className="border-stone-300"
                    data-testid="input-billing-min-pax"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="billing_menu_price">Menüpreis</Label>
                  <Input
                    id="billing_menu_price"
                    value={vaFacts.billing_menu_price}
                    onChange={(e) => handleChange('billing_menu_price', e.target.value)}
                    placeholder="z.B. 45,00 € p.P."
                    className="border-stone-300"
                    data-testid="input-billing-menu"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="billing_drinks">Getränkepauschale</Label>
                  <Input
                    id="billing_drinks"
                    value={vaFacts.billing_drinks}
                    onChange={(e) => handleChange('billing_drinks', e.target.value)}
                    placeholder="z.B. 25,00 € p.P."
                    className="border-stone-300"
                    data-testid="input-billing-drinks"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="billing_technical">Technik</Label>
                  <Input
                    id="billing_technical"
                    value={vaFacts.billing_technical}
                    onChange={(e) => handleChange('billing_technical', e.target.value)}
                    placeholder="z.B. 150,00 €"
                    className="border-stone-300"
                    data-testid="input-billing-technical"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="billing_deco">Deko</Label>
                  <Input
                    id="billing_deco"
                    value={vaFacts.billing_deco}
                    onChange={(e) => handleChange('billing_deco', e.target.value)}
                    placeholder="z.B. 100,00 €"
                    className="border-stone-300"
                    data-testid="input-billing-deco"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="billing_latenight">Latenight-Fee</Label>
                  <Input
                    id="billing_latenight"
                    value={vaFacts.billing_latenight}
                    onChange={(e) => handleChange('billing_latenight', e.target.value)}
                    placeholder="z.B. 200,00 €"
                    className="border-stone-300"
                    data-testid="input-billing-latenight"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="billing_artists">Künstler</Label>
                  <Input
                    id="billing_artists"
                    value={vaFacts.billing_artists}
                    onChange={(e) => handleChange('billing_artists', e.target.value)}
                    placeholder="z.B. 500,00 €"
                    className="border-stone-300"
                    data-testid="input-billing-artists"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="billing_other">Sonstiges</Label>
                  <Input
                    id="billing_other"
                    value={vaFacts.billing_other}
                    onChange={(e) => handleChange('billing_other', e.target.value)}
                    placeholder="z.B. Garderobe 50,00 €"
                    className="border-stone-300"
                    data-testid="input-billing-other"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="shadow-card">
            <CardHeader className="border-b border-stone-100 bg-stone-50/50">
              <CardTitle className="font-serif text-lg text-burgundy flex items-center gap-2">
                <Database className="h-5 w-5" />
                Kunde in Gastronovi
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="gastronovi_customer">In Gastronovi erfasst?</Label>
                  <Select
                    value={vaFacts.gastronovi_customer}
                    onValueChange={(value) => handleChange('gastronovi_customer', value)}
                  >
                    <SelectTrigger className="border-stone-300" data-testid="select-gastronovi">
                      <SelectValue placeholder="Auswählen" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Ja">Ja</SelectItem>
                      <SelectItem value="Nein">Nein</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {vaFacts.gastronovi_customer === 'Ja' && (
                  <div className="space-y-2">
                    <Label htmlFor="gastronovi_customer_number">Kundennummer</Label>
                    <Input
                      id="gastronovi_customer_number"
                      value={vaFacts.gastronovi_customer_number}
                      onChange={(e) => handleChange('gastronovi_customer_number', e.target.value)}
                      placeholder="z.B. 12345"
                      className="border-stone-300"
                      data-testid="input-gastronovi-number"
                    />
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Right Column - Details */}
        <div className="space-y-6">
          <Card className="shadow-card">
            <CardHeader className="border-b border-stone-100 bg-stone-50/50">
              <CardTitle className="font-serif text-lg text-burgundy flex items-center gap-2">
                <Clock className="h-5 w-5" />
                Ablauf
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <Textarea
                value={vaFacts.schedule}
                onChange={(e) => handleChange('schedule', e.target.value)}
                placeholder="18:00 Empfang mit Sektempfang&#10;18:30 Begrüßung durch Geschäftsführung&#10;19:00 Vorspeise wird serviert&#10;..."
                className="border-stone-300 min-h-[120px]"
                data-testid="input-schedule"
              />
            </CardContent>
          </Card>

          <Card className="shadow-card">
            <CardHeader className="border-b border-stone-100 bg-stone-50/50">
              <CardTitle className="font-serif text-lg text-burgundy flex items-center gap-2">
                <Utensils className="h-5 w-5" />
                Menü
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <MenuEditor
                value={vaFacts.menu}
                onChange={(value) => handleChange('menu', value)}
                placeholder="Menü eingeben... (H2/H3 für Überschriften verwenden)"
              />
              <p className="text-xs text-charcoal-400 mt-2">
                Tipp: Verwenden Sie <strong>H2</strong> für Hauptkategorien (z.B. "3-Gänge-Menü"), <strong>H3</strong> für Unterkategorien (z.B. "Vorspeise") und Listen für einzelne Gerichte.
              </p>
            </CardContent>
          </Card>

          <Card className="shadow-card">
            <CardHeader className="border-b border-stone-100 bg-stone-50/50">
              <CardTitle className="font-serif text-lg text-burgundy flex items-center gap-2">
                <Music className="h-5 w-5" />
                Künstler & Technik
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6 space-y-4">
              <div className="space-y-2">
                <Label htmlFor="artists">Künstler / Unterhaltung</Label>
                <Textarea
                  id="artists"
                  value={vaFacts.artists}
                  onChange={(e) => handleChange('artists', e.target.value)}
                  placeholder="Band, DJ, Showact, etc."
                  className="border-stone-300 min-h-[60px]"
                  data-testid="input-artists"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="technical">Technik</Label>
                <Textarea
                  id="technical"
                  value={vaFacts.technical}
                  onChange={(e) => handleChange('technical', e.target.value)}
                  placeholder="Mikrofon, Beamer, Leinwand, etc."
                  className="border-stone-300 min-h-[60px]"
                  data-testid="input-technical"
                />
              </div>
            </CardContent>
          </Card>

          <Card className="shadow-card">
            <CardHeader className="border-b border-stone-100 bg-stone-50/50">
              <CardTitle className="font-serif text-lg text-burgundy flex items-center gap-2">
                <Info className="h-5 w-5" />
                Sonstiges
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <Textarea
                value={vaFacts.other_info}
                onChange={(e) => handleChange('other_info', e.target.value)}
                placeholder="Besondere Wünsche, Allergien, Dekorationen, etc."
                className="border-stone-300 min-h-[80px]"
                data-testid="input-other"
              />
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default EditorPage;
